import { useState } from 'react';
import { api } from '../api/endpoints';
import type { Station } from '../api/types';
import { useToast } from './Toast';
import { Button, ErrorNote, Modal } from './ui';

// Powering a PC off is the one floor action that cannot be undone from here —
// someone has to walk over and press the button to get it back — so it always
// asks first. The backend refuses a seat with a customer on it; the dialog
// says so up front rather than letting staff find out from an error.
export function ShutdownDialog({
  station,
  onClose,
  onDone,
}: {
  station: Station;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const occupied = Boolean(station.active_session);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.stations.shutdown(station.station_id);
      // The backend message distinguishes an online agent from one that is
      // not reporting in, and staff need to know which.
      toast.success(
        station.agent.online
          ? `${station.name} is shutting down.`
          : `${station.name} is not reporting in. It will shut down if it checks in within a few minutes — otherwise switch it off at the machine.`,
      );
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Shut down ${station.name}?`} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {occupied ? (
          <ErrorNote>
            {station.active_session!.customer_name} is on this PC. Stop their session
            before shutting it down.
          </ErrorNote>
        ) : (
          <p className="text-sm text-slate-300">
            The screen locks now and the PC powers off within a few seconds.
            To use it again, someone has to switch it on at the machine.
          </p>
        )}
        {!occupied && !station.agent.online && (
          <p className="text-sm text-amber-200">
            This PC is not reporting in right now. It will only shut down if it
            checks in within the next few minutes.
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="danger"
            className="flex-1"
            disabled={busy || occupied}
            onClick={submit}
          >
            {busy ? 'Shutting down…' : 'Shut down'}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
